/**
 * Typed API client. Uses absolute origin from VITE_API_BASE in prod.
 * Memory note feedback_cf_pages_worker_api_base: Pages does NOT proxy /api/*
 * to a sibling Worker. Dev proxy in vite.config.ts handles localhost.
 *
 * Reads the CSRF cookie (apm_csrf) and echoes into X-Csrf-Token on mutating
 * requests. Server validates double-submit.
 */

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId: string | null,
    public docUrl?: string,
    /** Full server error payload — exposes extra fields (e.g. 409 conflict
     *  may include task_id of the already-converted target). */
    public detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(
    new RegExp('(^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'),
  );
  return m ? decodeURIComponent(m[2]!) : null;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('content-type') && init.body) {
    headers.set('content-type', 'application/json');
  }
  headers.set('apetech-version', '2026-05-21');
  const method = (init.method ?? 'GET').toUpperCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie('apm_csrf');
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ error: { code: 'unknown', message: res.statusText } }));
    const err = body.error ?? { code: 'unknown', message: res.statusText };
    throw new ApiError(
      res.status,
      err.code,
      err.message,
      res.headers.get('x-request-id'),
      err.doc_url,
      // Pass through the rest of the body so callers can read extras
      // (e.g. 409 conflict includes task_id of the existing target).
      body as Record<string, unknown>,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------- typed endpoints ----------

export type MeResponse = {
  user: {
    user_id: string;
    email: string;
    name: string | null;
    image_key: string | null;
    avatar_url: string | null;
  };
  workspaces: {
    ws_id: string;
    slug: string;
    name: string;
    role: string;
    image_key: string | null;
    emoji: string | null;
  }[];
  active_ws_id: string | null;
};

// ---------- workspace types ----------

export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';

export type WorkspaceDetail = {
  ws_id: string;
  slug: string;
  name: string;
  owner_user_id: string;
  quiet_hours_start_local: string | null;
  quiet_hours_end_local: string | null;
  timezone: string | null;
  image_key: string | null;
  emoji: string | null;
  role: WorkspaceRole | null;
};

export type MemberRow = {
  user_id: string;
  email: string;
  name: string | null;
  image_key: string | null;
  avatar_url: string | null;
  role: WorkspaceRole;
  joined_at: number;
};

export type InviteRow = {
  token_hash_prefix: string;
  invited_email: string;
  role: WorkspaceRole;
  target_type: 'workspace' | 'project';
  expires_at: string;
  created_at: string;
};

// ---------- project types ----------

export type ProjectMemberPreview = {
  user_id: string;
  email: string;
  role: WorkspaceRole;
};

export type ProjectSummary = {
  project_id: string;
  slug: string;
  name: string;
  color: string | null;
  icon: string | null;
  image_key: string | null;
  emoji: string | null;
  ws_id: string;
  role: WorkspaceRole;
  home: number; // 1 if home workspace, 0 if pinned
  created_at: number;
  closed_at: number | null;
  task_total: number;
  task_open: number;
  task_done: number;
  members_preview: ProjectMemberPreview[];
  member_count: number;
};

export type ProjectDetail = {
  project_id: string;
  slug: string;
  name: string;
  color: string | null;
  icon: string | null;
  image_key: string | null;
  emoji: string | null;
  ws_id: string;
  ws_name: string;
  ws_owner_user_id: string;
  created_by: string;
  created_at: number;
  role: WorkspaceRole | null;
  deleted_at?: number | null;
  /** Read-only state. When set, no new tasks/comments/notes/links can
   *  be created. Reopen restores full mutation access. */
  closed_at: number | null;
  closed_by: string | null;
};

export type AllProjectsRow = {
  project_id: string;
  slug: string;
  name: string;
  color: string | null;
  icon: string | null;
  image_key: string | null;
  emoji: string | null;
  home_ws_id: string;
  home_ws_name: string;
  home_ws_owner_user_id: string;
  home_ws_owner_email: string;
  my_role: WorkspaceRole;
  pinned_ws_id: string | null;
  pinned_at: number | null;
  created_at: number;
  closed_at: number | null;
  task_total: number;
  task_open: number;
  task_done: number;
  members_preview: ProjectMemberPreview[];
  member_count: number;
};

export type InvitePeek = {
  target_type: 'workspace' | 'project';
  target_id: string;
  workspace: { name: string; slug: string } | null;
  project: { name: string; slug: string } | null;
  role: WorkspaceRole;
  expires_at: string;
  invited_email: string;
};

// ---------- task types ----------

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'archived';
export type TaskPriority = 'p0' | 'p1' | 'p2' | 'p3';

export type TaskListRow = {
  task_id: string;
  project_id: string;
  parent_task_id: string | null;
  title: string;
  body_md: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_user_id: string | null;
  assignee_email: string | null;
  due_at: number | null;
  position: number;
  created_by: string;
  created_at: number;
  completed_at: number | null;
  parent_event_id: string | null;
  subtask_total: number;
  subtask_done: number;
};

export type TaskDetail = {
  task_id: string;
  project_id: string;
  ws_id: string;
  parent_task_id: string | null;
  title: string;
  body_md: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_user_id: string | null;
  due_at: number | null;
  parent_event_id: string | null;
  position: number;
  created_by: string;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
  deleted_at: number | null;
  deleted_by: string | null;
  subtasks: Array<{
    task_id: string;
    title: string;
    status: TaskStatus;
    priority: TaskPriority;
    assignee_user_id: string | null;
    due_at: number | null;
    position: number;
  }>;
};

export type NewTaskBody = {
  title: string;
  body_md?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  assignee_user_id?: string | null;
  due_at?: string | null;
  parent_task_id?: string | null;
};

// ---------- comment types ----------

export type ProjectComment = {
  comment_id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  body_md: string;
  created_at: number;
  updated_at: number;
  reply_to_comment_id: string | null;
};

export type ProjectNote = {
  note_id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  body_md: string;
  created_at: number;
  updated_at: number;
};

export type ProjectLink = {
  link_id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  label: string;
  url: string;
  created_at: number;
};

export type ProjectActivityItem = {
  id: string;
  timestamp: number;
  kind: 'task_created' | 'task_done' | 'chat_posted' | 'note_added' | 'link_added';
  actor_user_id: string;
  actor_email: string;
  actor_name: string | null;
  target_id: string;
  target_label: string;
};

// ---------- rule types ----------

export type RoutingRuleAction =
  | 'route_to_project'
  | 'create_task'
  | 'assign_only'
  | 'silent_log'
  | 'escalate'
  | 'drop';

export type RoutingRule = {
  rule_id: string;
  source_id: string;
  ws_id: string;
  name: string;
  priority: number;
  match_json: string;
  action: RoutingRuleAction;
  target_project_id: string | null;
  target_assignee_user_id: string | null;
  default_severity: EventSeverity | null;
  enabled: boolean;
  last_matched_at: number | null;
  match_count: number;
  created_at: number;
  updated_at: number;
};

export type NewRuleBody = {
  name: string;
  match_json: string;
  action: RoutingRuleAction;
  target_project_id?: string | null;
  target_assignee_user_id?: string | null;
  default_severity?: EventSeverity | null;
  enabled?: boolean;
  priority?: number;
};

export type TestPayload = {
  type: string;
  title: string;
  body?: string | null;
  severity?: EventSeverity;
  url?: string | null;
  actor?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
};

// ---------- source / event types ----------

export type SourceSummary = {
  source_id: string;
  name: string;
  slug: string;
  icon: string | null;
  color: string | null;
  sk_live_prefix: string;
  whsec_prefix: string;
  callback_url: string | null;
  last_event_at: number | null;
  created_at: number;
};

export type SourceDetail = SourceSummary & {
  ws_id: string;
  callback_secret_prefix: string | null;
  allowed_origins_json: string | null;
};

export type SourceCreated = SourceSummary & {
  ws_id: string;
  sk_live: string; // PLAINTEXT — only at creation
  whsec: string; // PLAINTEXT — only at creation
};

export type EventSeverity = 'info' | 'warn' | 'urgent';
export type EventStatus = 'new' | 'seen' | 'later' | 'done';

export type EventSummary = {
  event_id: string;
  source_id: string;
  source_name: string;
  source_icon: string | null;
  source_color: string | null;
  ws_id: string;
  external_id: string;
  type: string;
  title: string;
  body_md: string | null;
  severity: EventSeverity;
  url: string | null;
  status: EventStatus;
  assignee_user_id: string | null;
  project_id: string | null;
  task_id: string | null;
  snoozed_until: number | null;
  occurred_at: number | null;
  created_at: number;
};

export type EventDetail = EventSummary & {
  actor_json: string | null;
  metadata_json: string | null;
  version: number;
};

export type EventListFilters = {
  status?: EventStatus;
  source_id?: string;
  severity?: EventSeverity;
  /** Filter to events routed to this project (per-project inbox). */
  project_id?: string;
  /** "true" to only return events with task_id IS NULL (not yet converted). */
  unconverted?: boolean;
  assigned?: 'me' | 'unassigned' | 'anyone';
  cursor?: string;
  limit?: number;
};

// ---------- client ----------

export const apiClient = {
  health: () =>
    api<{ ok: true; version: string; environment: string; ts: string }>('/v1/health'),
  me: () => api<MeResponse>('/v1/me'),
  requestMagicLink: (email: string) =>
    api<{ ok: true }>('/v1/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  logout: () => api<{ ok: true }>('/v1/auth/logout', { method: 'POST' }),
  setActiveWorkspace: (ws_id: string) =>
    api<{ ok: true; active_ws_id: string }>('/v1/me/active-workspace', {
      method: 'POST',
      body: JSON.stringify({ ws_id }),
    }),
  createWorkspace: (name: string) =>
    api<{ ws_id: string; slug: string; name: string; role: WorkspaceRole }>(
      '/v1/workspaces',
      { method: 'POST', body: JSON.stringify({ name }) },
    ),
  currentWorkspace: () => api<WorkspaceDetail>('/v1/workspaces/current'),
  updateWorkspace: (
    patch: Partial<{
      name: string;
      quiet_hours_start_local: string | null;
      quiet_hours_end_local: string | null;
      timezone: string | null;
      emoji: string | null;
    }>,
  ) =>
    api<WorkspaceDetail>('/v1/workspaces/current', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  listMembers: () => api<{ members: MemberRow[] }>('/v1/workspaces/current/members'),
  updateMemberRole: (user_id: string, role: WorkspaceRole) =>
    api<{ ok: true }>(`/v1/workspaces/current/members/${user_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeMember: (user_id: string) =>
    api<{ ok: true }>(`/v1/workspaces/current/members/${user_id}`, { method: 'DELETE' }),
  listInvites: () => api<{ invites: InviteRow[] }>('/v1/workspaces/current/invites'),
  createInvite: (email: string, role: WorkspaceRole = 'member') =>
    api<{
      token_hash_prefix: string;
      invited_email: string;
      role: WorkspaceRole;
      expires_at: string;
    }>('/v1/workspaces/current/invites', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),
  revokeInvite: (token_hash_prefix: string) =>
    api<{ ok: true }>(`/v1/workspaces/current/invites/${token_hash_prefix}`, {
      method: 'DELETE',
    }),
  peekInvite: (token: string) =>
    api<InvitePeek>(`/v1/invites/${encodeURIComponent(token)}`),
  acceptInvite: (
    token: string,
    opts: { pin_ws_id?: string | null } = {},
  ) =>
    api<
      | { ok: true; target_type: 'workspace'; ws_id: string; role: WorkspaceRole }
      | { ok: true; target_type: 'project'; project_id: string; pinned_ws_id: string | null; role: WorkspaceRole }
    >(`/v1/invites/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: opts.pin_ws_id ? JSON.stringify({ pin_ws_id: opts.pin_ws_id }) : '{}',
    }),

  // ---------- projects ----------
  listProjects: () =>
    api<{ projects: ProjectSummary[] }>('/v1/projects'),
  allProjects: () =>
    api<{ projects: AllProjectsRow[] }>('/v1/me/projects'),
  createProject: (name: string, opts: { color?: string; icon?: string } = {}) =>
    api<{
      project_id: string;
      ws_id: string;
      slug: string;
      name: string;
      color: string | null;
      icon: string | null;
      role: WorkspaceRole;
    }>('/v1/projects', {
      method: 'POST',
      body: JSON.stringify({ name, ...opts }),
    }),
  getProject: (project_id: string) =>
    api<ProjectDetail>(`/v1/projects/${encodeURIComponent(project_id)}`),
  updateProject: (
    project_id: string,
    patch: Partial<{
      name: string;
      color: string | null;
      icon: string | null;
      emoji: string | null;
    }>,
  ) =>
    api<{ ok: true }>(`/v1/projects/${encodeURIComponent(project_id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  /**
   * Soft-deletes the project. Creator-only; caller must type the project's
   * exact name as confirmation_name. 30-day retention; restore via restoreProject().
   */
  deleteProject: (project_id: string, confirmation_name: string) =>
    api<{ ok: true; deleted_at: string; restorable_until: string }>(
      `/v1/projects/${encodeURIComponent(project_id)}`,
      { method: 'DELETE', body: JSON.stringify({ confirmation_name }) },
    ),
  restoreProject: (project_id: string) =>
    api<{ ok: true }>(`/v1/projects/${encodeURIComponent(project_id)}/restore`, {
      method: 'POST',
    }),
  closeProject: (project_id: string) =>
    api<{ ok: true }>(`/v1/projects/${encodeURIComponent(project_id)}/close`, {
      method: 'POST',
    }),
  reopenProject: (project_id: string) =>
    api<{ ok: true }>(`/v1/projects/${encodeURIComponent(project_id)}/reopen`, {
      method: 'POST',
    }),
  listTrash: () =>
    api<{
      projects: Array<{
        project_id: string;
        slug: string;
        name: string;
        ws_id: string;
        ws_name: string;
        created_by: string;
        deleted_at: number;
        deleted_by: string | null;
        is_creator: boolean;
        restorable_until: string;
      }>;
    }>('/v1/me/trash'),
  listProjectMembers: (project_id: string) =>
    api<{ members: MemberRow[] }>(`/v1/projects/${encodeURIComponent(project_id)}/members`),
  updateProjectMemberRole: (
    project_id: string,
    user_id: string,
    role: WorkspaceRole,
  ) =>
    api<{ ok: true }>(
      `/v1/projects/${encodeURIComponent(project_id)}/members/${encodeURIComponent(user_id)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    ),
  removeProjectMember: (project_id: string, user_id: string) =>
    api<{ ok: true }>(
      `/v1/projects/${encodeURIComponent(project_id)}/members/${encodeURIComponent(user_id)}`,
      { method: 'DELETE' },
    ),
  listProjectInvites: (project_id: string) =>
    api<{ invites: InviteRow[] }>(`/v1/projects/${encodeURIComponent(project_id)}/invites`),
  createProjectInvite: (project_id: string, email: string, role: WorkspaceRole = 'member') =>
    api<{
      token_hash_prefix: string;
      invited_email: string;
      role: WorkspaceRole;
      expires_at: string;
    }>(`/v1/projects/${encodeURIComponent(project_id)}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),
  revokeProjectInvite: (project_id: string, prefix: string) =>
    api<{ ok: true }>(
      `/v1/projects/${encodeURIComponent(project_id)}/invites/${encodeURIComponent(prefix)}`,
      { method: 'DELETE' },
    ),
  pinProject: (project_id: string, ws_id: string) =>
    api<{ ok: true; project_id: string; ws_id: string }>(
      `/v1/projects/${encodeURIComponent(project_id)}/pin`,
      { method: 'POST', body: JSON.stringify({ ws_id }) },
    ),
  unpinProject: (project_id: string) =>
    api<{ ok: true }>(`/v1/projects/${encodeURIComponent(project_id)}/pin`, {
      method: 'DELETE',
    }),
  uploadProjectImage: async (project_id: string, file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    const csrf = readCookie('apm_csrf');
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(project_id)}/image`,
      {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'x-csrf-token': csrf } : undefined,
        body: fd,
      },
    );
    if (!res.ok) {
      let msg = 'upload_failed';
      try {
        const j = (await res.json()) as { error?: { message?: string } };
        msg = j.error?.message ?? msg;
      } catch {/* noop */}
      throw new ApiError(res.status, 'upload_failed', msg, null);
    }
    return (await res.json()) as { image_key: string; image_url: string };
  },
  deleteProjectImage: (project_id: string) =>
    api<{ ok: true }>(`/v1/projects/${encodeURIComponent(project_id)}/image`, {
      method: 'DELETE',
    }),
  uploadWorkspaceImage: async (file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    const csrf = readCookie('apm_csrf');
    const res = await fetch(`${API_BASE}/v1/workspaces/current/image`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'x-csrf-token': csrf } : undefined,
      body: fd,
    });
    if (!res.ok) {
      let msg = 'upload_failed';
      try {
        const j = (await res.json()) as { error?: { message?: string } };
        msg = j.error?.message ?? msg;
      } catch {/* noop */}
      throw new ApiError(res.status, 'upload_failed', msg, null);
    }
    return (await res.json()) as { image_key: string; image_url: string };
  },
  deleteWorkspaceImage: () =>
    api<{ ok: true }>('/v1/workspaces/current/image', { method: 'DELETE' }),

  // ---------- user profile (me) ----------
  updateMe: (patch: { name?: string | null }) =>
    api<{ ok: true }>('/v1/me', { method: 'PATCH', body: JSON.stringify(patch) }),
  uploadMyImage: async (file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    const csrf = readCookie('apm_csrf');
    const res = await fetch(`${API_BASE}/v1/me/image`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'x-csrf-token': csrf } : undefined,
      body: fd,
    });
    if (!res.ok) {
      let msg = 'upload_failed';
      try {
        const j = (await res.json()) as { error?: { message?: string } };
        msg = j.error?.message ?? msg;
      } catch {/* noop */}
      throw new ApiError(res.status, 'upload_failed', msg, null);
    }
    return (await res.json()) as { image_key: string; image_url: string };
  },
  deleteMyImage: () => api<{ ok: true }>('/v1/me/image', { method: 'DELETE' }),

  // ---------- sources ----------
  listSources: () =>
    api<{ sources: SourceSummary[] }>('/v1/workspaces/current/sources'),
  createSource: (name: string, opts: { icon?: string | null; color?: string | null } = {}) =>
    api<SourceCreated>('/v1/workspaces/current/sources', {
      method: 'POST',
      body: JSON.stringify({ name, ...opts }),
    }),
  getSource: (source_id: string) =>
    api<SourceDetail>(`/v1/sources/${encodeURIComponent(source_id)}`),
  updateSource: (source_id: string, patch: Partial<{ name: string; icon: string | null; color: string | null; callback_url: string | null; allowed_origins: string[] | null }>) =>
    api<{ ok: true }>(`/v1/sources/${encodeURIComponent(source_id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteSource: (source_id: string) =>
    api<{ ok: true }>(`/v1/sources/${encodeURIComponent(source_id)}`, {
      method: 'DELETE',
    }),
  rotateSourceKey: (source_id: string) =>
    api<{ sk_live: string; sk_live_prefix: string }>(
      `/v1/sources/${encodeURIComponent(source_id)}/rotate-key`,
      { method: 'POST' },
    ),
  rotateSourceWhsec: (source_id: string) =>
    api<{ whsec: string; whsec_prefix: string }>(
      `/v1/sources/${encodeURIComponent(source_id)}/rotate-whsec`,
      { method: 'POST' },
    ),

  // ---------- routing rules ----------
  listRules: (source_id: string) =>
    api<{ rules: RoutingRule[] }>(
      `/v1/source/${encodeURIComponent(source_id)}/rules`,
    ),
  createRule: (source_id: string, body: NewRuleBody) =>
    api<{ rule_id: string; priority: number }>(
      `/v1/source/${encodeURIComponent(source_id)}/rules`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateRule: (rule_id: string, body: Partial<NewRuleBody>) =>
    api<{ ok: true }>(`/v1/rules/${encodeURIComponent(rule_id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteRule: (rule_id: string) =>
    api<{ ok: true }>(`/v1/rules/${encodeURIComponent(rule_id)}`, {
      method: 'DELETE',
    }),
  reorderRules: (source_id: string, rule_ids: string[]) =>
    api<{ ok: true }>(
      `/v1/source/${encodeURIComponent(source_id)}/rules/reorder`,
      { method: 'POST', body: JSON.stringify({ rule_ids }) },
    ),
  testRules: (source_id: string, payload: TestPayload) =>
    api<{
      matched: { rule_id: string; name: string; action: RoutingRuleAction } | null;
      trace: Array<{ rule_id: string; name: string; matched: boolean }>;
    }>(`/v1/source/${encodeURIComponent(source_id)}/rules/test`, {
      method: 'POST',
      body: JSON.stringify({ payload }),
    }),

  // ---------- events / inbox ----------
  listEvents: (filters: EventListFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.source_id) params.set('source_id', filters.source_id);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.project_id) params.set('project_id', filters.project_id);
    if (filters.unconverted) params.set('unconverted', 'true');
    if (filters.assigned) params.set('assigned', filters.assigned);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.limit) params.set('limit', filters.limit.toString());
    const qs = params.toString();
    return api<{ events: EventSummary[]; next_cursor: string | null }>(
      `/v1/events${qs ? `?${qs}` : ''}`,
    );
  },
  getEvent: (event_id: string) =>
    api<EventDetail>(`/v1/events/${encodeURIComponent(event_id)}`),
  updateEvent: (
    event_id: string,
    patch: Partial<{
      status: EventStatus;
      snoozed_until: string | null;
      assignee_user_id: string | null;
      project_id: string | null;
    }>,
  ) =>
    api<{ ok: true }>(`/v1/events/${encodeURIComponent(event_id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  redactEvent: (event_id: string) =>
    api<{ ok: true }>(`/v1/events/${encodeURIComponent(event_id)}/redact`, {
      method: 'POST',
    }),
  convertEventToTask: (
    event_id: string,
    body: { project_id?: string; parent_task_id?: string | null; priority?: TaskPriority; assignee_user_id?: string | null } = {},
  ) =>
    api<{ ok: true; task_id: string; project_id: string }>(
      `/v1/events/${encodeURIComponent(event_id)}/convert-to-task`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ---------- tasks ----------
  listTasks: (
    project_id: string,
    filters: { status?: TaskStatus; priority?: TaskPriority; assigned?: 'me' | 'unassigned' | 'anyone'; parent_task_id?: string } = {},
  ) => {
    const p = new URLSearchParams();
    if (filters.status) p.set('status', filters.status);
    if (filters.priority) p.set('priority', filters.priority);
    if (filters.assigned) p.set('assigned', filters.assigned);
    if (filters.parent_task_id) p.set('parent_task_id', filters.parent_task_id);
    const qs = p.toString();
    return api<{ tasks: TaskListRow[] }>(
      `/v1/project/${encodeURIComponent(project_id)}/tasks${qs ? `?${qs}` : ''}`,
    );
  },
  createTask: (project_id: string, body: NewTaskBody) =>
    api<TaskDetail>(`/v1/project/${encodeURIComponent(project_id)}/tasks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getTask: (task_id: string) =>
    api<TaskDetail>(`/v1/tasks/${encodeURIComponent(task_id)}`),
  updateTask: (task_id: string, patch: Partial<NewTaskBody>) =>
    api<TaskDetail>(`/v1/tasks/${encodeURIComponent(task_id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteTask: (task_id: string, confirmation_name: string) =>
    api<{ ok: true; deleted_at: string; restorable_until: string }>(
      `/v1/tasks/${encodeURIComponent(task_id)}`,
      { method: 'DELETE', body: JSON.stringify({ confirmation_name }) },
    ),
  restoreTask: (task_id: string) =>
    api<{ ok: true }>(`/v1/tasks/${encodeURIComponent(task_id)}/restore`, {
      method: 'POST',
    }),
  reorderTasks: (project_id: string, parent_task_id: string | null, task_ids: string[]) =>
    api<{ ok: true }>(`/v1/project/${encodeURIComponent(project_id)}/tasks/reorder`, {
      method: 'POST',
      body: JSON.stringify({ parent_task_id, task_ids }),
    }),
  myTasks: (status?: TaskStatus) =>
    api<{
      tasks: Array<{
        task_id: string;
        project_id: string;
        project_name: string;
        title: string;
        status: TaskStatus;
        priority: TaskPriority;
        due_at: number | null;
        created_at: number;
      }>;
    }>(`/v1/me/tasks${status ? `?status=${status}` : ''}`),

  // ---------- comments (project chat) ----------
  listProjectComments: (project_id: string) =>
    api<{ comments: ProjectComment[]; my_role: WorkspaceRole }>(
      `/v1/project/${encodeURIComponent(project_id)}/comments`,
    ),
  createProjectComment: (
    project_id: string,
    body_md: string,
    reply_to_comment_id?: string | null,
  ) =>
    api<{ comment: ProjectComment }>(
      `/v1/project/${encodeURIComponent(project_id)}/comments`,
      {
        method: 'POST',
        body: JSON.stringify(
          reply_to_comment_id ? { body_md, reply_to_comment_id } : { body_md },
        ),
      },
    ),
  editComment: (comment_id: string, body_md: string) =>
    api<{ comment: ProjectComment }>(`/v1/comments/${encodeURIComponent(comment_id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ body_md }),
    }),
  deleteComment: (comment_id: string) =>
    api<{ ok: true }>(`/v1/comments/${encodeURIComponent(comment_id)}`, {
      method: 'DELETE',
    }),

  // ---------- project notes ----------
  listProjectNotes: (project_id: string) =>
    api<{ notes: ProjectNote[]; my_role: WorkspaceRole }>(
      `/v1/project/${encodeURIComponent(project_id)}/notes`,
    ),
  createProjectNote: (project_id: string, body_md: string) =>
    api<{ note: ProjectNote }>(`/v1/project/${encodeURIComponent(project_id)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body_md }),
    }),
  editProjectNote: (note_id: string, body_md: string) =>
    api<{ ok: true; updated_at: number }>(`/v1/project-notes/${encodeURIComponent(note_id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ body_md }),
    }),
  deleteProjectNote: (note_id: string) =>
    api<{ ok: true }>(`/v1/project-notes/${encodeURIComponent(note_id)}`, {
      method: 'DELETE',
    }),

  // ---------- project links ----------
  listProjectLinks: (project_id: string) =>
    api<{ links: ProjectLink[]; my_role: WorkspaceRole }>(
      `/v1/project/${encodeURIComponent(project_id)}/links`,
    ),
  createProjectLink: (project_id: string, label: string, url: string) =>
    api<{ link: ProjectLink }>(`/v1/project/${encodeURIComponent(project_id)}/links`, {
      method: 'POST',
      body: JSON.stringify({ label, url }),
    }),
  deleteProjectLink: (link_id: string) =>
    api<{ ok: true }>(`/v1/project-links/${encodeURIComponent(link_id)}`, {
      method: 'DELETE',
    }),

  // ---------- project activity (synthesized timeline) ----------
  listProjectActivity: (project_id: string) =>
    api<{ items: ProjectActivityItem[] }>(
      `/v1/project/${encodeURIComponent(project_id)}/activity`,
    ),

  // ---------- telegram ----------
  telegramStatus: () =>
    api<{
      linked: boolean;
      telegram_username: string | null;
      linked_at: string | null;
      paused_until: string | null;
      timezone: string | null;
      quiet_start_local: string | null;
      quiet_end_local: string | null;
      tg_chat_batch_threshold: number;
      tg_chat_batch_minutes: number;
    }>('/v1/me/telegram/status'),
  telegramLinkToken: () =>
    api<{ deep_link: string; bot_username: string; expires_at: string }>(
      '/v1/me/telegram/link-token',
      { method: 'POST' },
    ),
  telegramUnlink: () =>
    api<{ ok: true }>('/v1/me/telegram', { method: 'DELETE' }),
  telegramUpdate: (patch: {
    timezone?: string | null;
    quiet_start_local?: string | null;
    quiet_end_local?: string | null;
    tg_chat_batch_threshold?: number;
    tg_chat_batch_minutes?: number;
  }) =>
    api<{ ok: true }>('/v1/me/telegram', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
};
